import autoBind from 'auto-bind';
import { pipe } from 'fp-ts/lib/function';
import { Configuration } from '../configuration/configuration.type';
import { FilterSnapshot } from '../features/filtering/FilterSnapshot';
import { IndexFiltersSnapshot } from '../features/indexFiltering/IndexFiltersSnapshot';
import { createSetControlsFromConfig } from '../state/createInitialState';
import { State } from '../state/types/state';
import { QuerySet_ControlsState } from '../state/types/state-controls.type';
import { SnapshotSet, SnapshotSets } from './types/snapshot-sets';

export class Snapshot {
    private filterSnapshot: FilterSnapshot;
    private indexFiltersSnapshot: IndexFiltersSnapshot;

    constructor() {
        this.filterSnapshot = new FilterSnapshot();
        this.indexFiltersSnapshot = new IndexFiltersSnapshot();

        autoBind(this);
    }

    public createControlsSnapshotFromState(sets: State['controls']): SnapshotSets {
        return pipe(this.filterSetsToSnapshot(sets), this.mapSetsStateToSnapshot);
    }

    public createControlsStateFromSnapshot(
        snapshotSets: SnapshotSets,
        configSets: Configuration['layout']['search']
    ): State['controls'] {
        return configSets.map((setConfig) => {
            return pipe(createSetControlsFromConfig(setConfig), (setControls: QuerySet_ControlsState) => {
                const snapshotSet = snapshotSets.find(
                    (snapshotSet) => setControls.querySet.identifier === snapshotSet.querySetIdentifier
                );

                if (snapshotSet) {
                    if (snapshotSet.filters) {
                        setControls.filters = this.filterSnapshot.createStateFromSnapshot(
                            snapshotSet.filters,
                            setControls.filters
                        );
                    }

                    if (snapshotSet.sorting) {
                        setControls.sorting = snapshotSet.sorting;
                    }

                    if (snapshotSet.indices && setControls.indices) {
                        setControls.indices = this.indexFiltersSnapshot.createStateFromSnapshot(
                            snapshotSet.indices,
                            setControls.indices
                        );
                    }
                }

                return setControls;
            });
        });
    }

    /**
     * Decide whether set should be included in the snapshot
     */
    private filterSetsToSnapshot(sets: State['controls']): State['controls'] {
        return sets.filter((set) => {
            return (
                (set.indices && this.indexFiltersSnapshot.includeSetInSnapshot(set.indices)) ||
                (set.filters.length > 0 && this.filterSnapshot.includeSetInSnapshot(set.filters)) ||
                set.sorting !== null
            );
        });
    }

    /**
     * Map the sets state to a snapshot
     */
    private mapSetsStateToSnapshot(sets: State['controls']): SnapshotSets {
        return sets.map((set) =>
            pipe(
                {
                    querySetIdentifier: set.querySet.identifier,
                },
                (setSnapshot: SnapshotSet) => {
                    if (set.indices) {
                        setSnapshot.indices = this.indexFiltersSnapshot.createSnapshotFromState(set.indices);
                    }
                    if (set.sorting) {
                        setSnapshot.sorting = set.sorting;
                    }

                    if (set.filters.length > 0) {
                        setSnapshot.filters = this.filterSnapshot.createSnapshotFromState(set.filters);
                    }

                    return setSnapshot;
                }
            )
        );
    }
}
